import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Box, Typography, Divider, Stack, Chip, Paper, Tabs, Tab, Tooltip, CircularProgress, ToggleButtonGroup, ToggleButton, Select, MenuItem, FormControl, InputLabel, Link, Dialog, Button, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Avatar from "@mui/material/Avatar";

import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PublicIcon from "@mui/icons-material/Public";
import BarChartIcon from "@mui/icons-material/BarChart";
import BookIcon from "@mui/icons-material/Book";
import ParkIcon from "@mui/icons-material/Park";
import UpdateIcon from "@mui/icons-material/Update";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import TimelineIcon from '@mui/icons-material/Timeline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import LanguageIcon from '@mui/icons-material/Language';
import MapIcon from '@mui/icons-material/Map';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Legend, Tooltip as RechartsTooltip, BarChart, Bar } from "recharts";

import supabase from "../../../../utils/supabase";
import RenderReferenceItem from "./TabContents/RenderReferenceItem";
import EcologyItem from "./TabContents/EcologyItem";
import GbifOccurrenceMap from "./TabContents/GbifOccurrenceMap"; // Leafletマップコンポーネントをインポート
import { SCIENTIFIC_NAME_DOC_TYPES } from "../../../../constants/scientificNameDocs";

const EmptyText = ({ children }) => (
  <Typography variant="body2" color="text.secondary" fontStyle="italic">
    {children}
  </Typography>
);

const SectionTitle = ({ icon, children }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    {icon}
    <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold' }}>
      {children}
    </Typography>
  </Stack>
);

const RANK_PRIORITY = {
  kingdom: 1, phylum: 2, class: 3, order: 4, superfamily: 5, family: 6,
  subfamily: 7, tribe: 8, genus: 9, subgenus: 10, species: 11,
  replacement_name: 11, subspecies: 12,
};

const VALID_CLASSIFICATION_RANKS = new Set([
  "kingdom",
  "phylum",
  "class",
  "order",
  "superfamily",
  "family",
  "subfamily",
  "tribe",
  "subtribe",
  "genus",
  "subgenus",
  "species",
  "subspecies",
]);

const normalizeRank = (rank) => (rank || "").trim().toLowerCase();
const isSpeciesOrSubspeciesRank = (rank) => {
  const r = normalizeRank(rank);
  return r === "species" || r === "subspecies";
};

const isInvalidRankName = (rank) => {
  const r = (rank || "").toLowerCase();
  return (
    r.includes("synonym") ||
    r.includes("syn.") ||
    r.includes(" syn ") ||
    r === "homonym" ||
    r === "invalid"
  );
};

const formatRankLabel = (rank) => {
  if (!rank) return "N/A";
  return rank
    .replace(/_/g, " ")
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ")
    .trim();
};

export default function DialogDisplayScientificNameDetails({
  initialScientificName,
  allScientificNames = [],
  scientificNameAuthors = [],
  allTaxonomicActs = [],
  allReferences = [],
  descendantCountMap = {},
  onNodeClick,
  onFetchChildren,
  fetchedChildren,
  isPage = false,
}) {
  const { t, i18n } = useTranslation(["name_detail_tab", "common"]);
  const [scientificName, setScientificName] = useState(initialScientificName);
  const [distributionData, setDistributionData] = useState([]); // bionomic_records(data_type='distribution')
  const [ecologyData, setEcologyData] = useState([]);           // bionomic_records(data_type='ecology')
  const [descendantCounts, setDescendantCounts] = useState({ valid: {}, invalid: {} });
  const [invalidDescendants, setInvalidDescendants] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [gbifMedia, setGbifMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [largeImage, setLargeImage] = useState(null);
  const [gbifSummary, setGbifSummary] = useState({ total: null, georeferenced: null, withImage: null });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [visibleMediaCount, setVisibleMediaCount] = useState(12);
  const [descendantTrendSource, setDescendantTrendSource] = useState([]);
  const [descendantTrendLoading, setDescendantTrendLoading] = useState(false);
  const [descendantTrendError, setDescendantTrendError] = useState(null);
  const [descendantTrendStatus, setDescendantTrendStatus] = useState("valid"); // valid | invalid | all
  const [descendantStartYear, setDescendantStartYear] = useState(null);
  const [descendantEndYear, setDescendantEndYear] = useState(null);
  const [descendantIncludePreStart, setDescendantIncludePreStart] = useState(true);
  const [descendantRankFilter, setDescendantRankFilter] = useState("all");
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState(null);
  const [docEditors, setDocEditors] = useState(new Map());
  const [actorNameMap, setActorNameMap] = useState(new Map());
  // DB に actor_id カラムが無い環境があるためデフォルトは false。存在が確認できたら true に切り替える。
  const [adminHasActorColumn, setAdminHasActorColumn] = useState(false);

  const mergeMemberNames = useCallback((members) => {
    if (!members) return;
    setActorNameMap((prev) => {
      const map = new Map(prev);
      (members || []).forEach((m) => {
        if (m.actor_id) map.set(m.actor_id, m.display_name);
        if (m.auth_user_id) map.set(m.auth_user_id, m.display_name);
        map.set(m.id, m.display_name);
      });
      return map;
    });
  }, []);

  const fetchMembersWithFallback = useCallback(async (ids = null) => {
    const hasIds = Array.isArray(ids) && ids.length > 0;
    const quoted = hasIds ? ids.map((id) => `"${id}"`).join(",") : "";
    const selectColsWithActor = "id, auth_user_id, actor_id, display_name";
    const selectColsNoActor = "id, auth_user_id, display_name";
    const buildQuery = (includeActor) => {
      let q = supabase.from("admin_team_members").select(includeActor ? selectColsWithActor : selectColsNoActor);
      if (hasIds) {
        const orClause = includeActor
          ? `id.in.(${quoted}),auth_user_id.in.(${quoted}),actor_id.in.(${quoted})`
          : `id.in.(${quoted}),auth_user_id.in.(${quoted})`;
        q = q.or(orClause);
      }
      return q;
    };

    const initialIncludeActor = adminHasActorColumn;
    const { data, error } = await buildQuery(initialIncludeActor);
    if (!error) return data;
    if (error.code !== "42703") throw error;
    // Column missing: flip flag and retry without actor_id
    setAdminHasActorColumn(false);
    const retry = await buildQuery(false);
    if (retry.error) throw retry.error;
    console.warn("admin_team_members.actor_id not found; falling back to auth_user_id/id");
    return retry.data;
  }, [adminHasActorColumn]);

  const resolveActorName = useCallback(
    (logOrId) => {
      const actorId = typeof logOrId === "string" ? logOrId : logOrId?.actor_id || logOrId?.updated_by;
      if (actorId && actorNameMap.has(actorId)) {
        const disp = actorNameMap.get(actorId);
        if (disp && typeof disp === "object") {
          const lang = (i18n.language || "").split("-")[0];
          return disp[lang] || disp.en || Object.values(disp)[0];
        }
        return disp;
      }
      const fallback = typeof logOrId === "object" ? null : null;
      return fallback || t("history.unknownUser");
    },
    [actorNameMap, i18n.language, t]
  );

  useEffect(() => {
    setScientificName(initialScientificName);
    setVisibleMediaCount(12); // Reset visible media count when the main subject changes
  }, [initialScientificName]);

  useEffect(() => {
    // Taxonomyページからのみ渡される onFetchChildren を使い、未取得の子ノードを読み込む
    // これにより、Synonymic Namesセクションが常に最新の情報を表示できるようになる
    // ScientificNameDetailsPageでは全件取得済みのため、この処理は不要
    if (scientificName?.id && onFetchChildren && fetchedChildren && !fetchedChildren.has(scientificName.id)) {
      onFetchChildren(scientificName.id, 0); // 最初のページを読み込む
    }
  }, [scientificName?.id, onFetchChildren, fetchedChildren]);

  useEffect(() => {
    let cancelled = false;
    const fetchDocs = async () => {
      if (!scientificName?.id) {
        setDocs([]);
        setDocsLoading(false);
        setDocsError(null);
        return;
      }
      setDocsLoading(true);
      setDocsError(null);
      const { data, error } = await supabase
        .from("scientific_name_docs")
        .select("id, doc_type, lang, body, updated_at, updated_by")
        .eq("scientific_name_id", scientificName.id);
      if (cancelled) return;
      if (error) {
        console.warn("[scientific_name_docs] fetch failed:", error.message);
        setDocsError(error.message);
        setDocs([]);
      } else {
        setDocs(data || []);
        const editorIds = Array.from(new Set((data || []).map((d) => d.updated_by).filter(Boolean)));
        if (editorIds.length) {
          try {
            const editors = await fetchMembersWithFallback(editorIds);
            mergeMemberNames(editors);
            const map = new Map();
            (editors || []).forEach((e) => {
              if (e.actor_id) map.set(e.actor_id, e.display_name);
              if (e.auth_user_id) map.set(e.auth_user_id, e.display_name);
              map.set(e.id, e.display_name);
            });
            setDocEditors(map);
          } catch (err) {
            console.error("failed to fetch doc editors", err);
            setDocEditors(new Map());
          }
        } else {
          setDocEditors(new Map());
        }
      }
      setDocsLoading(false);
    };
    fetchDocs();
    return () => {
      cancelled = true;
    };
  }, [scientificName?.id, i18n.language, fetchMembersWithFallback, mergeMemberNames]);

  const allScientificNamesMap = useMemo(
    () => new Map(allScientificNames.map((sn) => [sn.id, sn])),
    [allScientificNames]
  );

  const getParentNode = useCallback(
    (id) => {
      if (!id) return null;
      return allScientificNamesMap.get(id) || null;
    },
    [allScientificNamesMap]
  );

  const childrenMap = useMemo(() => {
    const m = new Map();
    allScientificNames.forEach((sn) => {
      if (!sn?.current_parent) return;
      if (!m.has(sn.current_parent)) m.set(sn.current_parent, []);
      m.get(sn.current_parent).push(sn);
    });
    return m;
  }, [allScientificNames]);

  const ancestors = useMemo(() => {
    if (!scientificName || !allScientificNamesMap.size) return [];
    const result = [];
    let cur = scientificName;
    const seen = new Set();
    while (cur?.current_parent && !seen.has(cur.current_parent)) {
      seen.add(cur.current_parent);
      const parent = allScientificNamesMap.get(cur.current_parent);
      if (!parent) break;
      result.unshift(parent);
      cur = parent;
    }
    return result;
  }, [scientificName, allScientificNamesMap]);

  const validNameNodeAncestors = useMemo(() => {
    const isInvalid = isInvalidRankName(scientificName.current_rank) || !!scientificName.current_invalid_type;
    const validNameNode = isInvalid && scientificName.current_parent ? allScientificNamesMap.get(scientificName.current_parent) : null;
    if (!validNameNode) return [];
    
    const result = [];
    let cur = validNameNode;
    const seen = new Set();
    while (cur?.current_parent && !seen.has(cur.current_parent)) {
        seen.add(cur.current_parent);
        const parent = allScientificNamesMap.get(cur.current_parent);
        if (!parent) break;
        result.unshift(parent);
        cur = parent;
    }
    return result;
  }, [scientificName, allScientificNamesMap]);

  useEffect(() => {
    // ユーザーの指摘に基づき、表示中の学名の直下の子要素のみをシノニムとしてリストアップするようにロジックを修正。
    // これにより、下位階層を辿る複雑な処理をなくし、他のタブから情報が引き継がれてしまう問題を根本的に解決します。
    const startNodeId = scientificName?.id;
    if (!startNodeId) {
      setInvalidDescendants([]);
      return;
    }

    const results = [];
    const directChildren = childrenMap.get(startNodeId) || [];

    for (const child of directChildren) {
      // この子の親が現在のタブの学名IDと一致し、かつ無効名であるかをチェックします。
      if (child.current_parent === startNodeId && (isInvalidRankName(child.current_rank) || child.current_invalid_type)) {
        results.push(child);
      }
    }
    setInvalidDescendants(results);
  }, [scientificName?.id, childrenMap]);

  const usageKeys = useMemo(() => {
    const key = scientificName?.gbif_usage_key;
    if (!key) return [];
    if (Array.isArray(key)) {
      return key.map(String).filter(Boolean);
    }
    // Handle legacy comma-separated string
    return String(key)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [scientificName?.gbif_usage_key]);

  const allGbifIds = useMemo(() => {
    if (!scientificName) return [];
    const ids = new Set();
    // 1. Usage Key (旧Backbone ID) を最優先。カンマ区切りも許容。
    usageKeys.forEach((v) => ids.add(v));
    // 2. Primary GBIF ID
    if (scientificName.gbif_id) {
      ids.add(String(scientificName.gbif_id));
    }

    // 3. Merged GBIF IDs
    let mergedIds = [];
    if (scientificName.merged_gbif_ids) {
      if (typeof scientificName.merged_gbif_ids === 'string') {
        try {
          const parsed = JSON.parse(scientificName.merged_gbif_ids);
          if (Array.isArray(parsed)) {
            mergedIds = parsed;
          }
        } catch (e) {
          // ignore parse error
        }
      } else if (Array.isArray(scientificName.merged_gbif_ids)) {
        mergedIds = scientificName.merged_gbif_ids;
      }
    }

    mergedIds.forEach(id => {
      if (id) ids.add(String(id));
    });

    const finalIds = Array.from(ids);
    return finalIds;
  }, [usageKeys, scientificName?.gbif_id, scientificName?.merged_gbif_ids]);
  const formatCurrentNameHTML = useCallback((nameNode, nameAncestors) => {
    if (!nameNode) return "";

    const italics = (n) => (n ? `<i>${n}</i>` : "");
    const rankNorm = normalizeRank(nameNode.current_rank);
    const epithet = nameNode.name_spell_original || nameNode.name_spell_valid || '';

    let nameStr = "";
    const italicRanks = new Set(["genus", "subgenus", "species", "subspecies"]);

    if (italicRanks.has(rankNorm)) {
        if (rankNorm === "species" || rankNorm === "subspecies") {
            const genus = nameAncestors.find((a) => normalizeRank(a.current_rank) === "genus");
            const subgenus = nameAncestors.find((a) => normalizeRank(a.current_rank) === "subgenus");
            const genusName = genus ? (genus.name_spell_original || genus.name_spell_valid) : '';
            const subgenusPart = subgenus ? `(${italics(subgenus.name_spell_original || subgenus.name_spell_valid)}) ` : "";

            if (rankNorm === "species") {
                nameStr = `${italics(genusName)} ${subgenusPart}${italics(epithet)}`.trim().replace(/\s+/g, ' ');
            } else { // subspecies
                const speciesAncestor = nameAncestors.find((a) => normalizeRank(a.current_rank) === "species");
                const speciesEpithet = speciesAncestor ? (speciesAncestor.name_spell_original || speciesAncestor.name_spell_valid) : '';
                nameStr = `${italics(genusName)} ${subgenusPart}${italics(speciesEpithet)} ${italics(epithet)}`.trim().replace(/\s+/g, ' ');
            }
        } else if (rankNorm === "subgenus") {
            const genus = nameAncestors.find((a) => normalizeRank(a.current_rank) === "genus");
            const genusName = genus ? (genus.name_spell_original || genus.name_spell_valid) : '';
            nameStr = `${italics(genusName)} (${italics(epithet)})`;
        } else { // genus
            nameStr = italics(epithet);
        }
    } else {
        nameStr = epithet;
    }
    
    if (!nameStr.replace(/<[^>]*>/g, '').trim()) {
        nameStr = epithet;
    }

    return nameStr;
  }, []);

  const formatSynonymNameHTML = useCallback((sn, allNamesMap) => {
    if (!sn) return "";

    const originalAncestors = [];
    let cur = sn;
    const seen = new Set();
    // Use original_parent to trace ancestors
    while (cur?.original_parent && !seen.has(cur.original_parent)) {
        seen.add(cur.original_parent);
        const parent = allNamesMap.get(cur.original_parent);
        if (!parent) break;
        originalAncestors.unshift(parent);
        cur = parent;
    }

    const italics = (n) => (n ? `<i>${n}</i>` : "");
    // Use original_rank, fallback to current_rank
    const rankNorm = (sn.original_rank || sn.current_rank || "").trim().toLowerCase();
    
    // Use name_spell_original
    const originalRaw = (sn.name_spell_original || sn.name_spell_valid || "").split("|")[0].trim();
    const originalNoAuth = originalRaw.replace(/\([^)]*\)/g, " ").replace(/\[.*?\]/g, ' ').trim();
    const tokens = originalNoAuth.split(/\s+/).filter(Boolean);
    
    const tokenGenus = tokens[0] || "";
    const tokenSpecies = tokens[1] || "";
    const tokenSubspecies = tokens[2] || "";

    let nameStr = "";

    const epithet = sn.name_spell_valid || "";

    if (["genus", "species", "subspecies"].includes(rankNorm)) {
        if (rankNorm === "species" || rankNorm === "subspecies") {
            const genus = originalAncestors.find((a) => (a.original_rank || a.current_rank || "").toLowerCase() === "genus");
            const subgenus = originalAncestors.find((a) => (a.original_rank || a.current_rank || "").toLowerCase() === "subgenus");
            const genusName = italics(genus?.name_spell_original || genus?.name_spell_valid || tokenGenus);
            const subgenusPart = subgenus ? `(${italics(subgenus?.name_spell_original || subgenus?.name_spell_valid)}) ` : "";

            if (rankNorm === "species") {
                const speciesEpithet = italics(epithet || tokenSpecies);
                nameStr = `${genusName} ${subgenusPart}${speciesEpithet}`.trim();
            } else { // subspecies
                const speciesAncestor = originalAncestors.find((a) => (a.original_rank || a.current_rank || "").toLowerCase() === "species");
                const speciesEpithet = italics(speciesAncestor?.name_spell_original || speciesAncestor?.name_spell_valid || tokenSpecies);
                const subspEpithet = italics(epithet || tokenSubspecies);
                nameStr = `${genusName} ${subgenusPart}${speciesEpithet} ${subspEpithet}`.trim();
            }
        } else { nameStr = italics(originalNoAuth || epithet); }
    } else if (rankNorm === "subgenus") {
        const genus = originalAncestors.find((a) => (a.original_rank || a.current_rank || "").toLowerCase() === "genus");
        const genusName = italics(genus?.name_spell_original || genus?.name_spell_valid || tokenGenus);
        nameStr = `${genusName} (${italics(originalNoAuth || epithet)})`;
    } else { nameStr = originalNoAuth || epithet; }
    
    if (!nameStr.replace(/<[^>]*>/g, '').trim()) { nameStr = italics(originalRaw); }

    return nameStr;
  }, []);

  const getAllDescendantSpeciesSubspeciesIds = useCallback((parentId, taxaMap) => {
    const descendantIds = new Set();
    const directChildrenMap = new Map();

    taxaMap.forEach((taxon) => {
      if (taxon.current_parent) {
        if (!directChildrenMap.has(taxon.current_parent)) {
          directChildrenMap.set(taxon.current_parent, []);
        }
        directChildrenMap.get(taxon.current_parent).push(taxon.id);
      }
    });

    const findDescendantsRecursive = (currentId) => {
      const childrenIds = directChildrenMap.get(currentId) || [];
      for (const childId of childrenIds) {
        const childTaxon = taxaMap.get(childId);
        if (childTaxon) {
          if (isSpeciesOrSubspeciesRank(childTaxon.current_rank)) {
            descendantIds.add(childId);
          }
          findDescendantsRecursive(childId);
        }
      }
    };
    findDescendantsRecursive(parentId);
    return Array.from(descendantIds);
  }, []);

  const fetchBionomicData = useCallback(async (taxaIds) => {
    if (!taxaIds || taxaIds.length === 0) {
      setDistributionData([]);
      setEcologyData([]);
      return;
    }
    try {
      const { data, error } = await supabase
        .from("bionomic_records")
        .select("*")
        .in("target_taxa_id", taxaIds);

      if (error) {
        console.error("Error fetching bionomic_records:", error);
        setDistributionData([]);
        setEcologyData([]);
        return;
      }

      const distribution = data.filter((item) => item.data_type === "distribution");
      const ecology = data.filter((item) => item.data_type !== "distribution");

      setDistributionData(distribution || []);
      setEcologyData(ecology || []);
    } catch (err) {
      console.error("Error fetching bionomic_records (catch):", err);
      setDistributionData([]);
      setEcologyData([]);
    }
  }, []);

  useEffect(() => {
    if (!scientificName) return;
    const descendantIds = getAllDescendantSpeciesSubspeciesIds(scientificName.id, allScientificNamesMap);
    fetchBionomicData(descendantIds);
  }, [scientificName, getAllDescendantSpeciesSubspeciesIds, allScientificNamesMap, fetchBionomicData]);

  useEffect(() => {
    let cancelled = false;
    const loadTimeline = async () => {
      if (!scientificName?.id) {
        setDescendantTrendSource([]);
        setDescendantStartYear(null);
        setDescendantEndYear(null);
        return;
      }
      setDescendantTrendLoading(true);
      setDescendantTrendError(null);
      
      try {
        let allData = [];
        let hasMore = true;
        let page = 0;
        const pageSize = 1000; // APIの制限に合わせる

        // データがなくなるまでループして取得
        while (hasMore) {
          const from = page * pageSize;
          const to = from + pageSize - 1;

          const { data, error } = await supabase
            .rpc("taxonomy_descendant_yearly_counts", { p_root_id: scientificName.id })
            .range(from, to);

          if (error) throw error;

          if (data && data.length > 0) {
            allData = allData.concat(data);
            // 取得できた数がpageSize未満なら、もうデータはないので終了
            if (data.length < pageSize) {
              hasMore = false;
            } else {
              page++; // 次のページへ
            }
          } else {
            hasMore = false;
          }
        }

        if (cancelled) return;
        
        console.log(`[Details] Total rows fetched: ${allData.length}`); // 1345 になるはず
        setDescendantTrendSource(allData);

      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setDescendantTrendError(err.message || t("errors.timelineLoad"));
        setDescendantTrendSource([]);
      } finally {
        if (!cancelled) setDescendantTrendLoading(false);
      }
    };
    loadTimeline();
    return () => { cancelled = true; };
  }, [scientificName?.id, t]);

  const descendantYearOptions = useMemo(() => {
    if (!descendantTrendSource.length) return [];
    const years = Array.from(
      new Set(
        descendantTrendSource
          .map((r) => r.year)
          .filter((y) => Number.isFinite(y))
      )
    ).sort((a, b) => a - b);
    return years;
  }, [descendantTrendSource]);

  useEffect(() => {
    if (!descendantYearOptions.length) {
      setDescendantStartYear(null);
      setDescendantEndYear(null);
      return;
    }
    const minYear = descendantYearOptions[0];
    const maxYear = descendantYearOptions[descendantYearOptions.length - 1];
    let newStart = Number.isFinite(descendantStartYear) ? descendantStartYear : minYear;
    newStart = Math.min(Math.max(newStart, minYear), maxYear);
    let newEnd = Number.isFinite(descendantEndYear) ? descendantEndYear : maxYear;
    newEnd = Math.min(Math.max(newEnd, minYear), maxYear);
    if (newEnd < newStart) newEnd = newStart;
    if (newStart !== descendantStartYear) setDescendantStartYear(newStart);
    if (newEnd !== descendantEndYear) setDescendantEndYear(newEnd);
  }, [descendantYearOptions, descendantStartYear, descendantEndYear]);

  const handleDescendantStartYearChange = (event) => {
    const newStart = event.target.value;
    setDescendantStartYear(newStart);
    if (Number.isFinite(descendantEndYear) && descendantEndYear < newStart) {
      setDescendantEndYear(newStart);
    }
  };

  const handleDescendantEndYearChange = (event) => {
    const newEnd = event.target.value;
    setDescendantEndYear(newEnd);
    if (Number.isFinite(descendantStartYear) && newEnd < descendantStartYear) {
      setDescendantStartYear(newEnd);
    }
  };

  useEffect(() => {
    if (!scientificName?.id) return;

    const fromMap = descendantCountMap?.[scientificName.id];
    if (fromMap) {
      const cleanValid = {};
      const cleanInvalid = { ...(fromMap.invalid || {}) };
      Object.entries(fromMap.valid || {}).forEach(([k, v]) => {
        if (isInvalidRankName(k)) cleanInvalid[k] = v;
        else cleanValid[k] = v;
      });
      setDescendantCounts({ valid: cleanValid, invalid: cleanInvalid });
      return;
    }

    const valid = {};
    const invalid = {};
    const traverse = (id) => {
      const kids = childrenMap.get(id) || [];
      kids.forEach((child) => {
        const rankRaw = normalizeRank(child.current_rank);
        if (!rankRaw) return;
        const rank = rankRaw.toLowerCase();
        if (isInvalidRankName(rank)) {
          invalid[rank] = (invalid[rank] || 0) + 1;
        } else {
          valid[rank] = (valid[rank] || 0) + 1;
        }
        traverse(child.id);
      });
    };
    traverse(scientificName.id);
    setDescendantCounts({ valid, invalid });
  }, [scientificName?.id, childrenMap, descendantCountMap, isInvalidRankName]);

  const authorsForThisScientificName = useMemo(() => {
    if (!scientificName || !scientificNameAuthors) return [];
    return scientificNameAuthors
      .filter((r) => r.scientific_name_id === scientificName.id)
      .sort((a, b) => a.author_order - b.author_order);
  }, [scientificName, scientificNameAuthors]);

  const formatAuthors = useCallback((rels) => {
    if (!rels?.length) return "";
    const names = rels
      .map((r) =>
        r.researcher?.last_name ||
        r.researcher?.last_name_eng ||
        r.researcher?.first_name ||
        r.author?.last_name_eng ||
        r.author?.last_name ||
        ""
      )
      .filter(Boolean);
    if (!names.length) return "";
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    return `${names.slice(0, -1).join(", ")} & ${names.at(-1)}`;
  }, []);

  // refs array
  const refsArray = useMemo(() => {
    return Array.isArray(allReferences?.data)
      ? allReferences.data
      : Array.isArray(allReferences)
      ? allReferences
      : [];
  }, [allReferences]);

  const distributionLocalities = useMemo(() => {
    const result = [];
    distributionData.forEach((rec) => {
      const recRefs = rec.source_publication_id
        ? refsArray.filter((r) => r.id === rec.source_publication_id)
        : [];
      result.push({ ...rec, references: recRefs });
    });
    return result;
  }, [distributionData, refsArray]);

  const ecologyLocalities = useMemo(() => {
    const result = [];
    ecologyData.forEach((rec) => {
      const recRefs = rec.source_publication_id
        ? refsArray.filter((r) => r.id === rec.source_publication_id)
        : [];
      result.push({ ...rec, references: recRefs });
    });
    return result;
  }, [ecologyData, refsArray]);

  const descendantTrendSummary = useMemo(() => {
    const start = Number.isFinite(descendantStartYear) ? descendantStartYear : null;
    const end = Number.isFinite(descendantEndYear) ? descendantEndYear : null;
    if (!descendantTrendSource.length) {
      return { chartData: [], ranks: [], yearRange: [null, null], total: 0, groupedByYear: new Map() };
    }

    const filtered = descendantTrendSource.filter((row) => {
      const rankNorm = normalizeRank(row.rank);
      const status = VALID_CLASSIFICATION_RANKS.has(rankNorm) ? "valid" : "invalid";
      if (descendantTrendStatus === "valid") return status === "valid";
      if (descendantTrendStatus === "invalid") return status === "invalid";
      return true;
    });
    if (!filtered.length) {
      return { chartData: [], ranks: [], yearRange: [null, null], total: 0, groupedByYear: new Map() };
    }

    const ranks = Array.from(new Set(filtered.map((r) => r.rank || "unranked")));
    ranks.sort((a, b) => {
      const pa = RANK_PRIORITY[a] || 999;
      const pb = RANK_PRIORITY[b] || 999;
      if (pa !== pb) return pa - pb;
      return a.localeCompare(b);
    });

    const groupedAllYears = new Map();
    filtered.forEach(({ year, rank, total }) => {
      if (!Number.isFinite(year)) return;
      if (!groupedAllYears.has(year)) groupedAllYears.set(year, {});
      const bucket = groupedAllYears.get(year);
      bucket[rank] = (bucket[rank] || 0) + (total || 0);
    });

    const allYearsSorted = Array.from(groupedAllYears.keys()).sort((a, b) => a - b);
    if (!allYearsSorted.length) {
      return { chartData: [], ranks: [], yearRange: [null, null], total: 0, groupedByYear: new Map() };
    }
    const minYear = allYearsSorted[0];
    const maxYear = allYearsSorted[allYearsSorted.length - 1];
    const effectiveStart = start !== null ? Math.max(start, minYear) : minYear;
    const effectiveEnd = end !== null ? Math.min(end, maxYear) : maxYear;

    const groupedByYear = new Map();
    for (let y = effectiveStart; y <= effectiveEnd; y += 1) {
      const counts = groupedAllYears.get(y) || {};
      groupedByYear.set(y, counts);
    }

    const years = Array.from(groupedByYear.keys()).sort((a, b) => a - b);
    const cumulative = {};

    if (descendantIncludePreStart && start !== null) {
      groupedAllYears.forEach((counts, year) => {
        if (year >= start) return;
        ranks.forEach((rank) => {
          const inc = counts[rank] || 0;
          cumulative[rank] = (cumulative[rank] || 0) + inc;
        });
      });
    }

    const chartData = years.map((year) => {
      const point = { year };
      ranks.forEach((rank) => {
        const increment = groupedByYear.get(year)?.[rank] || 0;
        cumulative[rank] = (cumulative[rank] || 0) + increment;
        point[rank] = cumulative[rank];
      });
      point.total = Object.values(cumulative).reduce((sum, val) => sum + (typeof val === "number" ? val : 0), 0);
      return point;
    });

    const total = chartData.length ? chartData[chartData.length - 1].total : 0;
    return { chartData, ranks, yearRange: [years[0] ?? null, years[years.length - 1] ?? null], total, groupedByYear };
  }, [descendantTrendSource, descendantTrendStatus, descendantStartYear, descendantEndYear, descendantIncludePreStart]);

  const filteredRanks = useMemo(() => {
    if (!descendantTrendSummary.ranks?.length) return [];
    if (descendantRankFilter === "all") return descendantTrendSummary.ranks;
    return descendantTrendSummary.ranks.filter((r) => r === descendantRankFilter);
  }, [descendantTrendSummary.ranks, descendantRankFilter]);

  useEffect(() => {
    if (descendantRankFilter === "all") return;
    if (!descendantTrendSummary.ranks.includes(descendantRankFilter)) {
      setDescendantRankFilter("all");
    }
  }, [descendantTrendSummary.ranks, descendantRankFilter]);

  const descendantAnnualSummary = useMemo(() => {
    const grouped = descendantTrendSummary.groupedByYear;
    const ranks = descendantTrendSummary.ranks || [];
    if (!grouped || grouped.size === 0 || !ranks.length) {
      return { chartData: [], ranks: [] };
    }
    const years = Array.from(grouped.keys()).sort((a, b) => a - b);
    const chartData = years.map((year) => {
      const counts = grouped.get(year) || {};
      const point = { year };
      ranks.forEach((rank) => {
        point[rank] = counts[rank] || 0;
      });
      point.total = ranks.reduce((sum, rank) => sum + (counts[rank] || 0), 0);
      return point;
    });
    return { chartData, ranks };
  }, [descendantTrendSummary]);

  const descendantValidCounts = useMemo(() => {
    if (!descendantCounts?.valid) return [];
    return Object.entries(descendantCounts.valid).sort((a, b) => RANK_PRIORITY[a[0]] - RANK_PRIORITY[b[0]]);
  }, [descendantCounts]);

  const descendantInvalidCounts = useMemo(() => {
    if (!descendantCounts?.invalid) return [];
    return Object.entries(descendantCounts.invalid).sort((a, b) => a[0].localeCompare(b[0]));
  }, [descendantCounts]);

  const descendantRows = useMemo(() => {
    const rows = [];
    descendantValidCounts.forEach(([rank, count]) => {
      rows.push({ rank, count, status: "Valid" });
    });
    descendantInvalidCounts.forEach(([rank, count]) => {
      rows.push({ rank, count, status: "Invalid" });
    });
    return rows;
  }, [descendantValidCounts, descendantInvalidCounts]);

  const [metricMode, setMetricMode] = useState("both"); // both | valid | invalid

  const filteredDescendantRows = useMemo(() => {
    if (metricMode === "both") return descendantRows;
    return descendantRows.filter((r) => r.status.toLowerCase() === metricMode);
  }, [descendantRows, metricMode]);

  // --- Metrics Tab: Country Distribution ---
  const [countryMetricMode, setCountryMetricMode] = useState("both"); // both | valid | invalid
  const [countryRankFilter, setCountryRankFilter] = useState("all"); // all | species | subspecies | etc.

  const countryDistributionCounts = useMemo(() => {
    if (!distributionData.length) return [];

    const counts = new Map();
    distributionData.forEach(rec => {
      const taxon = allScientificNamesMap.get(rec.target_taxa_id);
      if (!taxon) return;

      const status = isInvalidRankName(taxon.current_rank) ? 'invalid' : 'valid';
      if (countryMetricMode !== 'both' && countryMetricMode !== status) {
        return;
      }

      const rank = normalizeRank(taxon.current_rank);
      if (countryRankFilter !== 'all' && countryRankFilter !== rank) {
        return;
      }

      const country = rec.locality_text || 'Unknown';
      if (!counts.has(country)) {
        counts.set(country, 0);
      }
      counts.set(country, counts.get(country) + 1);
    });

    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [distributionData, allScientificNamesMap, countryMetricMode, countryRankFilter]);

  const availableRanksForFilter = useMemo(() => {
    const ranks = new Set();
    distributionData.forEach(rec => {
      const taxon = allScientificNamesMap.get(rec.target_taxa_id);
      if (taxon?.current_rank && !isInvalidRankName(taxon.current_rank)) {
        ranks.add(normalizeRank(taxon.current_rank));
      }
    });
    return ['all', ...Array.from(ranks).sort((a, b) => (RANK_PRIORITY[a] || 99) - (RANK_PRIORITY[b] || 99))];
  }, [distributionData, allScientificNamesMap]);

  // --- Metrics Tab: Data Completeness ---
  const dataCompleteness = useMemo(() => {
    if (!scientificName) return [];
    return [
      { label: t("completeness.authorship"), value: authorsForThisScientificName.length > 0, help: t("completeness.authorshipHelp") },
      { label: t("completeness.publicationYear"), value: !!scientificName.authority_year, help: t("completeness.publicationYearHelp") },
      { label: t("completeness.typeLocality"), value: !!scientificName.type_locality, help: t("completeness.typeLocalityHelp") },
      { label: t("completeness.originalDescription"), value: !!scientificName.source_of_original_description, help: t("completeness.originalDescriptionHelp") },
    ];
  }, [scientificName, authorsForThisScientificName, t]);

  const pieData = useMemo(() => {
    return filteredDescendantRows.filter((r) => r.count > 0);
  }, [filteredDescendantRows]);

  const totalCount = useMemo(
    () => pieData.reduce((sum, r) => sum + (r.count || 0), 0),
    [pieData]
  );

  // --- Metrics Tab: Data Freshness ---
  const dataFreshness = useMemo(() => {
    if (auditLoading) return { level: 'loading', text: t("freshness.loading") };
    if (!auditLogs || auditLogs.length === 0) {
      return { level: 'unknown', text: t("freshness.noHistory"), color: 'default' };
    }
    const latestLog = auditLogs[0];
    const lastUpdate = new Date(latestLog.changed_at || latestLog.created_at);
    const diffDays = (new Date() - lastUpdate) / (1000 * 60 * 60 * 24);

    if (diffDays <= 30) return { level: 'high', text: t("freshness.high", { days: Math.floor(diffDays) }), color: 'success' };
    if (diffDays <= 365) return { level: 'medium', text: t("freshness.medium", { months: Math.floor(diffDays / 30) }), color: 'warning' };
    return { level: 'low', text: t("freshness.low"), color: 'error' };
  }, [auditLogs, auditLoading, t]);

  const COLORS = ["#4fc3f7", "#9575cd", "#ba68c8", "#ffb74d", "#81c784", "#e57373", "#64b5f6", "#f06292", "#aed581", "#ff8a65", "#7986cb", "#4db6ac", "#dce775", "#ffcc80", "#a5d6a7"];

  const buildArcPath = (cx, cy, r, startAngle, endAngle) => {
    const largeArc = endAngle - startAngle <= Math.PI ? 0 : 1;
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  const PieChart = ({ size = 220, innerRadius = 0 }) => {
    if (!pieData.length || totalCount === 0) return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: size }}>
        <Typography variant="body2" color="text.secondary">{t("empty.noData")}</Typography>
      </Box>
    );
    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2 - 8;
    let currentAngle = -Math.PI / 2;
    return (
      <Box sx={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size}>
          {pieData.map((slice, idx) => {
            const angle = (slice.count / totalCount) * Math.PI * 2;
            const path = buildArcPath(cx, cy, radius, currentAngle, currentAngle + angle);
            const color = COLORS[idx % COLORS.length];
            currentAngle += angle;
            return <path key={`${slice.rank}-${idx}`} d={path} fill={color} stroke="none" />;
          })}
          {innerRadius > 0 && (
            <circle cx={cx} cy={cy} r={innerRadius} fill="#121212" />
          )}
        </svg>
      </Box>
    );
  };

  const tabs = useMemo(() => ([
    { key: "overview", icon: <FactCheckIcon fontSize="small" />, label: t("tabs.overview") },
    { key: "systematics", icon: <AccountTreeIcon fontSize="small" />, label: t("tabs.systematics") },
    { key: "metrics", icon: <BarChartIcon fontSize="small" />, label: t("tabs.metrics") },
    { key: "gbif", icon: <LanguageIcon fontSize="small" />, label: t("tabs.gbif") },
    { key: "distribution", icon: <PublicIcon fontSize="small" />, label: t("tabs.distribution") },
    { key: "ecology", icon: <ParkIcon fontSize="small" />, label: t("tabs.ecology") },
    { key: "references", icon: <BookIcon fontSize="small" />, label: t("tabs.references") },
    { key: "history", icon: <UpdateIcon fontSize="small" />, label: t("tabs.history") },
  ]), [t]);

  const parseJsonSafe = useCallback((v) => {
    if (!v) return null;
    if (typeof v === "object") return v;
    try { return JSON.parse(v); } catch { return null; }
  }, []);

  const formatAuditLabel = useCallback((log) => {
    const table = (log?.table_name || "").toLowerCase();
    const action = (log?.action || "").toLowerCase();
    const after = parseJsonSafe(log?.after_data);
    const before = parseJsonSafe(log?.before_data);
    if (table === "scientific_names") {
      const name = after?.name_spell_valid || before?.name_spell_valid || log?.row_id || t("history.scientificNameFallback");
      return t("history.scientificNameLabel", { name });
    }
    if (table === "scientific_name_docs") {
      const doc = after || before || {};
      const docType = doc.doc_type || t("history.note");
      const label = `${docType}`;
      return t("history.taxonNoteLabel", { label });
    }
    return t("history.genericChange", { action: log.action || t("history.change"), table: log.table_name || t("history.unknown"), id: log.row_id ? ` (ID: ${log.row_id})` : "" });
  }, [parseJsonSafe, t]);

  const pickDocByType = useCallback((docType) => {
    if (!docs?.length) return null;
    const langPref = (i18n.language || "").split("-")[0] || i18n.language || "";
    const candidates = docs.filter((d) => d.doc_type === docType);
    if (!candidates.length) return null;
    const exactLang = candidates.find((d) => (d.lang || "").toLowerCase().startsWith(langPref.toLowerCase()));
    const english = candidates.find((d) => (d.lang || "").toLowerCase().startsWith("en"));
    return exactLang || english || candidates[0];
  }, [docs, i18n.language]);

  const sanitizeDocHtml = useCallback((html = "") => {
    const fallback = (s = "") =>
      s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    if (typeof window === "undefined" || typeof DOMParser === "undefined") return fallback(html);
    const parser = new DOMParser();
    const doc = parser.parseFromString(html || "", "text/html");
    const allowedTags = new Set(["b", "strong", "i", "em", "u", "p", "br", "ul", "ol", "li", "blockquote", "a"]);
    const walk = (node) => {
      const children = Array.from(node.childNodes);
      for (const child of children) {
        if (child.nodeType === 1) {
          const tag = child.tagName.toLowerCase();
          if (!allowedTags.has(tag)) {
            child.replaceWith(...Array.from(child.childNodes));
            continue;
          }
          Array.from(child.attributes).forEach((attr) => {
            const name = attr.name.toLowerCase();
            if (tag === "a" && name === "href") {
              const href = child.getAttribute("href") || "";
              if (!/^https?:\/\//i.test(href) && !href.startsWith("/researcher/")) {
                child.removeAttribute("href");
              } else {
                child.setAttribute("rel", "noreferrer");
                child.setAttribute("target", "_blank");
              }
            } else {
              child.removeAttribute(attr.name);
            }
          });
          walk(child);
        }
      }
    };
    walk(doc.body);
    return doc.body.innerHTML;
  }, []);

  const formatDateTime = useCallback(
    (value) => {
      if (!value) return "";
      try {
        const d = new Date(value);
        return d.toLocaleString(i18n.language || "ja-JP", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return value;
      }
    },
    [i18n.language]
  );

  useEffect(() => {
    let cancelled = false;
    const fetchAudit = async () => {
      if (!scientificName?.id) {
        setAuditLogs([]);
        return;
      }
      setAuditLoading(true);
      const fetchLogs = async () =>
        supabase
          .from("audit_log")
          .select("row_id, table_name, action, actor_id, before_data, after_data, created_at")
          .in("table_name", ["scientific_names", "scientific_name_docs"])
          .order("created_at", { ascending: false })
          .limit(2000);

      const res = await fetchLogs();
      if (res.error) {
        console.warn("[audit_log] fetch failed:", res.error.message);
        setAuditLoading(false);
        return;
      }
      if (!cancelled) {
        const logs = res.data || [];
        const filtered = logs.filter((l) => {
          const table = (l.table_name || "").toLowerCase();
          if (table === "scientific_names") {
            return String(l.row_id) === String(scientificName.id);
          }
          if (table === "scientific_name_docs") {
            const after = parseJsonSafe(l.after_data);
            const before = parseJsonSafe(l.before_data);
            const sid = after?.scientific_name_id || before?.scientific_name_id;
            return sid && String(sid) === String(scientificName.id);
          }
          return false;
        });
        setAuditLogs(filtered);
        const actorIds = Array.from(new Set(filtered.map((l) => l.actor_id).filter(Boolean)));
        if (actorIds.length) {
          try {
            const members = await fetchMembersWithFallback(actorIds);
            mergeMemberNames(members);
          } catch (err) {
            console.error("failed to fetch actor names", err);
          }
        }
        setAuditLoading(false);
      }
    };
    fetchAudit();
    return () => { cancelled = true; };
  }, [scientificName?.id, fetchMembersWithFallback, mergeMemberNames]);

  useEffect(() => {
    if (activeTab !== 'gbif' || !allGbifIds.length) {
      return;
    }

    const fetchMedia = async () => {
      console.log('[Details Media] Fetching media for IDs:', allGbifIds);
      setMediaLoading(true);
      setGbifMedia([]);
      if (!allGbifIds.length) {
        setMediaLoading(false);
        return;
      }
      try {
        // The /species/{key}/media endpoint is often empty.
        // It's more reliable to search for occurrences that have media.
        const taxonKeyParams = allGbifIds.map(id => `taxonKey=${id}`).join('&');
        const url = `https://api.gbif.org/v1/occurrence/search?${taxonKeyParams}&mediaType=StillImage&limit=100`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`GBIF media search failed with status ${response.status}`);
        const data = await response.json();

        const allMedia = data.results.flatMap(occurrence => occurrence.media || []);
        const imageMedia = allMedia.filter(m =>
          m.type === 'StillImage' && m.identifier && m.identifier.startsWith('http')
        );
        const uniqueImages = Array.from(new Map(imageMedia.map(m => [m.identifier, m])).values());
        console.log('[Details Media] Fetched unique images:', uniqueImages);
        setGbifMedia(uniqueImages);
      } catch (e) {
        console.error("Failed to fetch GBIF media", e);
      } finally {
        setMediaLoading(false);
      }
    };

    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const fetchCount = async (params) => {
          const uniqueIds = [...new Set(allGbifIds)];
          const counts = await Promise.all(
            uniqueIds.map(id =>
              fetch(`https://api.gbif.org/v1/occurrence/search?limit=0&taxonKey=${id}${params}`)
                .then(res => res.ok ? res.json() : { count: 0 })
                .then(data => data.count || 0)
            )
          );
          return counts.reduce((sum, count) => sum + count, 0);
        };

        const total = await fetchCount("");
        const georeferenced = await fetchCount("&hasGeospatialIssue=false&hasCoordinate=true");
        const withImage = await fetchCount("&mediaType=StillImage");

        setGbifSummary({ total, georeferenced, withImage });

      } catch (e) {
        console.error("Failed to fetch GBIF summary", e);
        setGbifSummary({ total: null, georeferenced: null, withImage: null });
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchSummary();
    fetchMedia();
  }, [activeTab, allGbifIds]);
  // No data
  if (!scientificName) {
    return (
      <Box sx={{ p: 3 }}>
        <EmptyText>{t("empty.noScientificName")}</EmptyText>
      </Box>
    );
  }

  const mapKeys = allGbifIds;

  const isInvalid = isInvalidRankName(scientificName.current_rank) || !!scientificName.current_invalid_type;
  const isSpeciesOrLower = isSpeciesOrSubspeciesRank(scientificName.current_rank);
  const validNameNode = isInvalid && scientificName.current_parent ? allScientificNamesMap.get(scientificName.current_parent) : null;

  return (
    <Box sx={{
      width: "100%",
      height: isPage ? "auto" : "100%",
      overflow: isPage ? "visible" : "hidden",
      p: isPage ? 2 : 0, // ページ表示の際は少しパディングを追加
      pt: isPage ? 0 : 0,
      display: "flex",
      flexDirection: "column"
    }}>
      <Box sx={{ p: isPage ? 0 : 2, pb: 1, pt: isPage ? 1 : 2 }}>

      </Box>
      {/* タブは固定ヘッダーに配置し、コンテンツのみスクロール */}
      <Box sx={{ flex: "0 0 auto" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 0.1 }}
        >
          {tabs.map((t) => (
            <Tab key={t.key} icon={t.icon} iconPosition="start" label={t.label} value={t.key} />
          ))}
        </Tabs>

        <Divider sx={{ mb: 0.1 }} />
      </Box>

      <Box sx={{
        flex: "1 1 auto",
        overflowY: isPage ? "visible" : "auto",
        minHeight: isPage ? 'auto' : 0,
        pt: 2, // タブとコンテンツの間にスペース
      }}>
        {activeTab === "systematics" && (
          <Stack spacing={2}>
            {/* --- Classification Section --- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <SectionTitle icon={<AccountTreeIcon />}>{t("sections.classification")}</SectionTitle>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {ancestors.map((ancestor, index) => {
                  const itemContent = (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: index * 2, py: 0.5, pr: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>└</Typography>
                      <Typography variant="body2" noWrap>
                        {ancestor.name_spell_valid}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        ({formatRankLabel(ancestor.current_rank)})
                      </Typography>
                    </Stack>
                  );
                  return onNodeClick ? (
                    <Box
                      key={ancestor.id}
                      onClick={() => onNodeClick(ancestor)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'rgba(127,255,212,0.1)' }
                      }}
                    >
                      {itemContent}
                    </Box>
                  ) : (
                    <Link key={ancestor.id} component={RouterLink} to={`/scientific-name/${ancestor.id}`} sx={{ textDecoration: 'none', color: 'inherit' }}>
                      {itemContent}
                    </Link>
                  );
                })}
                {/* Current Taxon */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: ancestors.length * 2, py: 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>└</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {scientificName.name_spell_valid}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({formatRankLabel(scientificName.current_rank)})
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            {/* --- Combination History / Affiliation Section --- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <SectionTitle icon={<AccountTreeIcon />}>{t("sections.combinationHistory")}</SectionTitle>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {[
                  { key: "original_parent", label: t("labels.originalParent"), value: scientificName.original_parent },
                  { key: "current_parent", label: t("labels.currentParent"), value: scientificName.current_parent },
                ].map((item) => {
                  const parentNode = getParentNode(item.value);
                  const content = parentNode ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {parentNode.name_spell_valid}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({formatRankLabel(parentNode.current_rank)})
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("empty.noData")}
                      </Typography>
                    </Stack>
                  );
                  if (parentNode && onNodeClick) {
                    return (
                      <Box
                        key={item.key}
                        onClick={() => onNodeClick(parentNode)}
                        sx={{ cursor: "pointer", borderRadius: 1, "&:hover": { bgcolor: "rgba(127,255,212,0.08)" }, px: 0.5, py: 0.3 }}
                      >
                        {content}
                      </Box>
                    );
                  }
                  if (parentNode) {
                    return (
                      <Link
                        key={item.key}
                        component={RouterLink}
                        to={`/scientific-name/${parentNode.id}`}
                        sx={{ textDecoration: "none", color: "inherit" }}
                      >
                        {content}
                      </Link>
                    );
                  }
                  return <Box key={item.key}>{content}</Box>;
                })}
              </Stack>
            </Paper>

            {/* --- Synonymic Names Section --- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <SectionTitle icon={<PlaylistRemoveIcon />}>{t("sections.synonymicNames")}</SectionTitle>
              {invalidDescendants.length === 0 ? (
                <EmptyText>{t("empty.noSynonyms")}</EmptyText>
              ) : (
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {invalidDescendants.map(synonym => {
                    const synonymAuthors = scientificNameAuthors
                      .filter(r => r.scientific_name_id === synonym.id)
                      .sort((a, b) => (a.author_order ?? 0) - (b.author_order ?? 0));
                    const authorText = formatAuthors(synonymAuthors);
                    const authority = [authorText, synonym.authority_year].filter(Boolean).join(', ');
                    const nameHTML = formatSynonymNameHTML(synonym, allScientificNamesMap);
                    return (
                      <Box key={synonym.id} sx={{ borderLeft: 3, borderColor: 'divider', pl: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          <Typography component="span" dangerouslySetInnerHTML={{ __html: nameHTML }} />
                          {authority && ` ${authority}`}
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                          <Chip size="small" label={formatRankLabel(synonym.current_rank || synonym.current_invalid_type)} />
                          {synonym.page && <Typography variant="caption" color="text.secondary">{t("labels.page", { value: synonym.page })}</Typography>}
                          {synonym.type_locality && <Typography variant="caption" color="text.secondary">{t("labels.typeLocality", { value: synonym.type_locality })}</Typography>}
                          {synonym.type_repository_id && <Typography variant="caption" color="text.secondary">{t("labels.repository", { value: synonym.type_repository_id })}</Typography>}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Stack>
        )}

        {activeTab === "metrics" && (
          <Stack spacing={1.5}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <SectionTitle icon={<TimelineIcon />}>{t("sections.cumulativeTimeline")}</SectionTitle>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                <Typography variant="body2">{t("controls.status")}</Typography>
                <ToggleButtonGroup
                  value={descendantTrendStatus}
                  exclusive
                  size="small"
                  onChange={(_, v) => v && setDescendantTrendStatus(v)}
                >
                  <ToggleButton value="valid">{t("controls.valid")}</ToggleButton>
                  <ToggleButton value="invalid">{t("controls.invalid")}</ToggleButton>
                  <ToggleButton value="all">{t("controls.all")}</ToggleButton>
                </ToggleButtonGroup>
                {descendantTrendSummary.total > 0 && (
                  <Chip size="small" color="primary" label={t("chips.totalNames", { count: descendantTrendSummary.total.toLocaleString() })} />
                )}
                {descendantTrendSummary.yearRange[0] && (
                  <Chip
                    size="small"
                    label={`${descendantTrendSummary.yearRange[0]} - ${descendantTrendSummary.yearRange[1] || descendantTrendSummary.yearRange[0]}`}
                    variant="outlined"
                  />
                )}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t("controls.startYear")}</InputLabel>
                  <Select
                    value={descendantStartYear ?? ""}
                    label={t("controls.startYear")}
                    onChange={handleDescendantStartYearChange}
                    disabled={!descendantYearOptions.length}
                  >
                    {descendantYearOptions.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t("controls.endYear")}</InputLabel>
                  <Select
                    value={descendantEndYear ?? ""}
                    label={t("controls.endYear")}
                    onChange={handleDescendantEndYearChange}
                    disabled={!descendantYearOptions.length}
                  >
                    {descendantYearOptions.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>{t("controls.rank")}</InputLabel>
                  <Select
                    value={descendantRankFilter}
                    label={t("controls.rank")}
                    onChange={(e) => setDescendantRankFilter(e.target.value)}
                    disabled={!descendantTrendSummary.ranks.length}
                  >
                    <MenuItem value="all">{t("controls.all")}</MenuItem>
                    {descendantTrendSummary.ranks.map((r) => (
                      <MenuItem key={r} value={r}>{formatRankLabel(r)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Switch checked={descendantIncludePreStart} onChange={(e) => setDescendantIncludePreStart(e.target.checked)} />}
                  label={t("controls.includePreStart")}
                />
              </Stack>
              {descendantTrendLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : descendantTrendError ? (
                <EmptyText>{descendantTrendError}</EmptyText>
              ) : descendantTrendSummary.chartData.length > 0 ? (
                <Box sx={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={descendantTrendSummary.chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip
                        wrapperStyle={{ zIndex: 1400 }}
                        contentStyle={{
                          backgroundColor: "rgba(30,30,30,0.95)",
                          border: "1px solid rgba(127,255,212,0.3)",
                        }}
                      />
                      <Legend />
                      {filteredRanks.map((rank, idx) => (
                        <Area
                          key={rank}
                          type="monotone"
                          dataKey={rank}
                          stackId="1"
                          stroke={COLORS[idx % COLORS.length]}
                          fill={COLORS[idx % COLORS.length]}
                          name={formatRankLabel(rank)}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <EmptyText>{t("empty.noDatedDescendants")}</EmptyText>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <SectionTitle icon={<BarChartIcon />}>{t("sections.annualTimeline")}</SectionTitle>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                <Typography variant="body2">{t("controls.status")}</Typography>
                <ToggleButtonGroup
                  value={descendantTrendStatus}
                  exclusive
                  size="small"
                  onChange={(_, v) => v && setDescendantTrendStatus(v)}
                >
                  <ToggleButton value="valid">{t("controls.valid")}</ToggleButton>
                  <ToggleButton value="invalid">{t("controls.invalid")}</ToggleButton>
                  <ToggleButton value="all">{t("controls.all")}</ToggleButton>
                </ToggleButtonGroup>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t("controls.startYear")}</InputLabel>
                  <Select
                    value={descendantStartYear ?? ""}
                    label={t("controls.startYear")}
                    onChange={handleDescendantStartYearChange}
                    disabled={!descendantYearOptions.length}
                  >
                    {descendantYearOptions.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t("controls.endYear")}</InputLabel>
                  <Select
                    value={descendantEndYear ?? ""}
                    label={t("controls.endYear")}
                    onChange={handleDescendantEndYearChange}
                    disabled={!descendantYearOptions.length}
                  >
                    {descendantYearOptions.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>{t("controls.rank")}</InputLabel>
                  <Select
                    value={descendantRankFilter}
                    label={t("controls.rank")}
                    onChange={(e) => setDescendantRankFilter(e.target.value)}
                    disabled={!descendantAnnualSummary.ranks.length}
                  >
                    <MenuItem value="all">{t("controls.all")}</MenuItem>
                    {descendantAnnualSummary.ranks.map((r) => (
                      <MenuItem key={r} value={r}>{formatRankLabel(r)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              {descendantTrendLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : descendantTrendError ? (
                <EmptyText>{descendantTrendError}</EmptyText>
              ) : descendantAnnualSummary.chartData.length > 0 ? (
                <Box sx={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={descendantAnnualSummary.chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip
                        wrapperStyle={{ zIndex: 1400 }}
                        contentStyle={{
                          backgroundColor: "rgba(30,30,30,0.95)",
                          border: "1px solid rgba(127,255,212,0.3)",
                        }}
                      />
                      <Legend />
                      {filteredRanks.map((rank, idx) => (
                        <Bar
                          key={rank}
                          dataKey={rank}
                          stackId="yearly"
                          fill={COLORS[idx % COLORS.length]}
                          name={formatRankLabel(rank)}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <EmptyText>{t("empty.noYearlyCounts")}</EmptyText>
              )}
            </Paper>

            {totalCount > 0 && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <SectionTitle icon={<BarChartIcon />}>{t("sections.namesIncluded")}</SectionTitle>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                  <Typography variant="body2">{t("controls.filter")}</Typography>
                  <ToggleButtonGroup value={metricMode} exclusive size="small" onChange={(_, v) => v && setMetricMode(v)}>
                    <ToggleButton value="both">{t("controls.both")}</ToggleButton>
                    <ToggleButton value="valid">{t("controls.valid")}</ToggleButton>
                    <ToggleButton value="invalid">{t("controls.invalid")}</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, minWidth: 200 }}>
                    <PieChart size={240} />
                    <Chip label={t("chips.totalNames", { count: totalCount.toLocaleString() })} color="primary" sx={{ fontSize: "1rem", fontWeight: 700, height: 32, "& .MuiChip-label": { px: 1.2 } }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200, maxHeight: 300, overflowY: 'auto' }}>
                    {filteredDescendantRows.length === 0 ? <EmptyText>{t("empty.noData")}</EmptyText> : (
                      <Stack spacing={0.6}>
                        {filteredDescendantRows.map((row, idx) => (
                          <Stack key={`${row.rank}-${row.status}-${idx}`} direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 0.75 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: COLORS[idx % COLORS.length] }} />
                              <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{formatRankLabel(row.rank)}</Typography>
                              <Chip size="small" label={row.status === "Valid" ? t("controls.valid") : t("controls.invalid")} color={row.status === "Valid" ? "success" : "default"} sx={{ height: 22 }} />
                            </Stack>
                            <Typography variant="body2" fontWeight={700}>{row.count}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <SectionTitle icon={<PublicIcon />}>{t("sections.namesByCountry")}</SectionTitle>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                <ToggleButtonGroup value={countryMetricMode} exclusive size="small" onChange={(_, v) => v && setCountryMetricMode(v)}>
                  <ToggleButton value="both">{t("controls.both")}</ToggleButton>
                  <ToggleButton value="valid">{t("controls.valid")}</ToggleButton>
                  <ToggleButton value="invalid">{t("controls.invalid")}</ToggleButton>
                </ToggleButtonGroup>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t("controls.rank")}</InputLabel>
                  <Select value={countryRankFilter} label={t("controls.rank")} onChange={(e) => setCountryRankFilter(e.target.value)}>
                    {availableRanksForFilter.map(r => <MenuItem key={r} value={r}>{r === "all" ? t("controls.all") : formatRankLabel(r)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {countryDistributionCounts.length === 0 ? <EmptyText>{t("empty.noDistributionData")}</EmptyText> : (
                  <Stack spacing={0.5}>
                    {countryDistributionCounts.map((item, idx) => {
                      const maxCount = countryDistributionCounts[0].count;
                      const barWidth = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                      return (
                        <Box key={idx}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">{item.name}</Typography>
                            <Typography variant="body2" fontWeight={700}>{item.count}</Typography>
                          </Stack>
                          <Box sx={{ height: 4, width: '100%', bgcolor: 'divider', borderRadius: 2, mt: 0.2 }}>
                            <Box sx={{ height: 4, width: `${barWidth}%`, bgcolor: 'primary.main', borderRadius: 2 }} />
                          </Box>
                        </Box>
                      )
                    })}
                  </Stack>
                )}
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <SectionTitle icon={<FactCheckIcon />}>{t("sections.dataCompleteness")}</SectionTitle>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {dataCompleteness.map(item => (
                  <Tooltip key={item.label} title={item.help} placement="top-start">
                    <Stack direction="row" spacing={1} alignItems="center">
                      {item.value ? <CheckCircleOutlineIcon color="success" fontSize="small" /> : <HighlightOffIcon color="error" fontSize="small" />}
                      <Typography variant="body2">{item.label}</Typography>
                    </Stack>
                  </Tooltip>
                ))}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <SectionTitle icon={<HistoryToggleOffIcon />}>{t("sections.dataFreshness")}</SectionTitle>
              <Box sx={{ mt: 1 }}>{dataFreshness.level === 'loading' ? <CircularProgress size={24} /> : (
                <Chip
                  label={dataFreshness.text}
                  color={dataFreshness.color}
                  sx={{ fontWeight: 700 }}
                />
              )}</Box>
            </Paper>
          </Stack>
        )}

        {activeTab === "gbif" && (
          <Stack spacing={2}>
            {usageKeys.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <SectionTitle icon={<VerifiedUserIcon />}>{t("sections.gbifUsageKey")}</SectionTitle>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                  {usageKeys.map((id) => (
                    <Chip
                      key={id}
                      label={id}
                      color={usageKeys.includes(String(id)) ? "success" : "default"}
                      component="a"
                      href={`https://www.gbif.org/species/${id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                      variant={usageKeys.includes(String(id)) ? "filled" : "outlined"}
                    />
                  ))}
                </Stack>
              </Paper>
            )}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <SectionTitle icon={<BarChartIcon />}>{t("sections.gbifSummary")}</SectionTitle>
              {summaryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', pt: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Stack direction="row" spacing={2} justifyContent="space-around" sx={{ mt: 1.5, textAlign: 'center' }}>
                  <Box>
                    <Typography variant="h6" component="div" fontWeight="bold">{gbifSummary.total?.toLocaleString() ?? '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{t("gbif.totalRecords")}</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box>
                    <Typography variant="h6" component="div" fontWeight="bold">{gbifSummary.georeferenced?.toLocaleString() ?? '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{t("gbif.georeferenced")}</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box>
                    <Typography variant="h6" component="div" fontWeight="bold">{gbifSummary.withImage?.toLocaleString() ?? '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{t("gbif.withImage")}</Typography>
                  </Box>
                </Stack>
              )}
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.5, height: { xs: 400, md: 600 }, display: 'flex', flexDirection: 'column' }}>
              <SectionTitle icon={<MapIcon />}>{t("sections.gbifMap")}</SectionTitle>
              {scientificName.gbif_usage_key || allGbifIds.length > 0 ? (
                <Box sx={{ flexGrow: 1, mt: 1 }}>
                  <GbifOccurrenceMap taxonKeys={mapKeys} />
                </Box>
              ) : <EmptyText>{t("empty.noMapData")}</EmptyText>}
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <SectionTitle icon={<PhotoLibraryIcon />}>{t("sections.gbifMedia")}</SectionTitle>
                {!mediaLoading && gbifMedia.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {t("gbif.total", { count: gbifMedia.length.toLocaleString() })}
                  </Typography>
                )}
              </Stack>
              {mediaLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box> : <>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {gbifMedia.slice(0, visibleMediaCount).map(media => {
                    const thumbnailUrl = `https://api.gbif.org/v1/image/cache/300x200/${encodeURIComponent(media.identifier)}`;
                    const tooltipContent = (
                      <Stack spacing={0.5} sx={{ p: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold">{media.title || t("gbif.untitled")}</Typography>
                        {media.creator && <Typography variant="caption">{t("gbif.creator", { value: media.creator })}</Typography>}
                        {media.publisher && <Typography variant="caption">{t("gbif.publisher", { value: media.publisher })}</Typography>}
                        {media.license && <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{t("gbif.license", { value: media.license })}</Typography>}
                      </Stack>
                    );
                    return (
                      <Grid item xs={6} sm={4} md={3} key={media.identifier}>
                        <Tooltip title={tooltipContent}>
                          <Box
                            component="img"
                            src={thumbnailUrl}
                            alt={media.title || 'GBIF Media'}
                            onClick={() => setLargeImage(media)}
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                            sx={{
                              width: '100%', height: 120, objectFit: 'cover', borderRadius: 1,
                              cursor: 'pointer', '&:hover': { opacity: 0.8, boxShadow: 3 }
                            }}
                          />
                        </Tooltip>
                      </Grid>
                    );
                  })}
                  {gbifMedia.length === 0 && !mediaLoading && <Grid item xs={12}><EmptyText>{t("empty.noMedia")}</EmptyText></Grid>}
                </Grid>
                {gbifMedia.length > visibleMediaCount && (
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button onClick={() => setVisibleMediaCount(prev => prev + 12)}>{t("controls.viewMore", { count: gbifMedia.length - visibleMediaCount })}</Button>
                  </Box>
                )}
              </>}
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <SectionTitle icon={<LanguageIcon />}>{t("sections.allGbifIds")}</SectionTitle>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                {allGbifIds.length > 0 ? allGbifIds.map(id => (
                  <Chip
                    key={id}
                    label={id}
                    icon={usageKeys.includes(String(id)) ? <VerifiedUserIcon /> : undefined}
                    color={usageKeys.includes(String(id)) ? "success" : "default"}
                    component="a"
                    href={`https://www.gbif.org/species/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    variant="outlined"
                  />
                )) : <EmptyText>{t("empty.noGbifIds")}</EmptyText>}
              </Stack>
            </Paper>
          </Stack>
        )}

        {activeTab === "distribution" && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionTitle icon={<PublicIcon />}>{t("sections.distribution")}</SectionTitle>
              {distributionLocalities.length === 0 ? (
                <EmptyText>{t("empty.noDistributionRecords")}</EmptyText>
              ) : (
                distributionLocalities.map((item, idx) => (
                  <EcologyItem key={idx} item={item} />
                ))
              )}
            </Paper>
          </Stack>
        )}

        {activeTab === "ecology" && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionTitle icon={<ParkIcon />}>{t("sections.ecology")}</SectionTitle>
              {ecologyLocalities.length === 0 ? (
                <EmptyText>{t("empty.noEcologyRecords")}</EmptyText>
              ) : (
                ecologyLocalities.map((item, idx) => (
                  <EcologyItem key={idx} item={item} />
                ))
              )}
            </Paper>
          </Stack>
        )}

        {activeTab === "overview" && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionTitle icon={<FactCheckIcon />}>{t("sections.overview")}</SectionTitle>
              {docsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  {SCIENTIFIC_NAME_DOC_TYPES.map((docDef) => {
                    const doc = pickDocByType(docDef.key);
                    if (!doc) {
                      return (
                        <Box key={docDef.key}>
                          <Typography variant="subtitle2">{t(`docs.${docDef.key}`, { defaultValue: docDef.fallbackTitle })}</Typography>
                          <EmptyText>{docsError ? docsError : t("empty.noDocs")}</EmptyText>
                        </Box>
                      );
                    }
                    return (
                      <Box key={docDef.key}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {t(`docs.${docDef.key}`, { defaultValue: docDef.fallbackTitle })}
                        </Typography>
                        <Box
                          sx={{ mt: 0.5 }}
                          dangerouslySetInnerHTML={{ __html: sanitizeDocHtml(doc.body || "") }}
                        />
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                          {doc.lang && <Chip size="small" label={(doc.lang || "").toUpperCase()} />}
                          {(doc.updated_at || doc.updated_by) && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              {doc.updated_by && (
                                <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                                  {(resolveActorName(doc.updated_by) || " ").slice(0, 2).toUpperCase()}
                                </Avatar>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {doc.updated_by ? resolveActorName(doc.updated_by) || t("history.unknownUser") : t("history.unknownUser")}
                                {doc.updated_at ? ` · ${formatDateTime(doc.updated_at)}` : ""}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Stack>
        )}

        {activeTab === "references" && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionTitle icon={<BookIcon />}>{t("sections.references")}</SectionTitle>
              {refsArray.length === 0 ? (
                <EmptyText>{t("empty.noReferences")}</EmptyText>
              ) : (
                <Grid container spacing={2}>
                  {refsArray.map((ref, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <RenderReferenceItem reference={ref} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Stack>
        )}

        {activeTab === "docs" && (
          <Stack spacing={2}>
            {SCIENTIFIC_NAME_DOC_TYPES.map((docDef) => {
              const doc = pickDocByType(docDef.key);
              return (
                <Paper key={docDef.key} variant="outlined" sx={{ p: 2 }}>
                  <SectionTitle icon={<FactCheckIcon />}>{t(`docs.${docDef.key}`, { defaultValue: docDef.fallbackTitle })}</SectionTitle>
                  {docsLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : doc ? (
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                        {doc.body}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", gap: 0.5 }}>
                        {doc.lang && <Chip size="small" label={(doc.lang || "").toUpperCase()} />}
                        {doc.updated_at && (
                          <Typography variant="caption" color="text.secondary">
                            {t("docs.lastUpdated", { date: new Date(doc.updated_at).toLocaleString() })}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  ) : (
                    <EmptyText>{docsError ? docsError : t("empty.noDocs")}</EmptyText>
                  )}
                </Paper>
              );
            })}
          </Stack>
        )}

        {activeTab === "history" && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionTitle icon={<UpdateIcon />}>{t("sections.dataHistory")}</SectionTitle>
              {auditLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : auditLogs.length === 0 ? (
                <EmptyText>{t("empty.noHistory")}</EmptyText>
              ) : (
                <Stack spacing={1.2}>
                  {auditLogs.map((log, idx) => {
                    const who = resolveActorName(log);
                    const lower = (who || "").toLowerCase();
                    const isBot = lower.includes("bot");
                    const whoLabel = isBot && lower.includes("hierarchical_import")
                      ? t("history.botHierarchicalImport")
                      : who;
                    const actionKey = (log.action || "-").toUpperCase();
                    const actionLabel = (() => {
                      if (actionKey === "DELETE") return t("history.action.delete", "DELETE");
                      if (actionKey === "INSERT") return t("history.action.insert", "INSERT");
                      if (actionKey === "UPDATE") return t("history.action.update", "UPDATE");
                      if (actionKey === "ADD") return t("history.action.add", "ADD");
                      return t("history.action.unknown", "UNKNOWN");
                    })();
                    const normalized = actionKey === "ADD" ? "INSERT" : actionKey;
                    const actionColor =
                      normalized === "INSERT" ? "success" :
                      normalized === "UPDATE" ? "warning" :
                      normalized === "DELETE" ? "error" : "default";
                    return (
                    <Box key={`${log.row_id}-${log.changed_at || log.created_at || idx}-${idx}`} sx={{ p: 1.2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip
                          size="small"
                          label={new Date(log.changed_at || log.created_at).toLocaleString()}
                          sx={{ height: 22 }}
                        />
                        <Chip
                          size="small"
                          label={actionLabel}
                          color={actionColor}
                          variant="outlined"
                          sx={{ height: 22 }}
                        />
                        <Chip
                          size="small"
                          label={whoLabel}
                          color={isBot ? "default" : "primary"}
                          icon={isBot ? <SmartToyIcon fontSize="small" /> : undefined}
                          sx={{ height: 22, "& .MuiChip-icon": { ml: 0.2 } }}
                        />
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {formatAuditLabel(log)}
                      </Typography>
                    </Box>
                  )})}
                </Stack>
              )}
            </Paper>
          </Stack>
        )}
      </Box>

      <Dialog open={!!largeImage} onClose={() => setLargeImage(null)} maxWidth="lg" fullWidth>
        {largeImage && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              {largeImage.title || t("gbif.imageDetails")}
            </DialogTitle>
            <DialogContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: '1 1 70%', minWidth: 0 }}>
                  <Box
                    component="img"
                    src={`https://api.gbif.org/v1/image/cache/1280x/${encodeURIComponent(largeImage.identifier)}`}
                    alt={largeImage.title || t("gbif.enlargedMedia")}
                    sx={{ width: '100%', height: 'auto', maxHeight: '70vh', objectFit: 'contain', borderRadius: 1, bgcolor: 'rgba(0,0,0,0.1)' }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 30%', minWidth: 250, maxHeight: '70vh', overflowY: 'auto' }}>
                  <Typography variant="h6" gutterBottom>{t("gbif.metadata")}</Typography>
                  <Stack spacing={1.5}>
                    {largeImage.creator && <Box><Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>{t("gbif.creatorLabel")}</Typography><Typography variant="body2">{largeImage.creator}</Typography></Box>}
                    {largeImage.publisher && <Box><Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>{t("gbif.publisherLabel")}</Typography><Typography variant="body2">{largeImage.publisher}</Typography></Box>}
                    {largeImage.license && <Box><Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>{t("gbif.licenseLabel")}</Typography><Link href={largeImage.license} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ wordBreak: 'break-all' }}>{largeImage.license}</Link></Box>}
                    {largeImage.references && <Box><Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>{t("gbif.sourceRecord")}</Typography><Link href={largeImage.references} target="_blank" rel="noopener noreferrer" variant="body2">{t("gbif.viewOnGbif")}</Link></Box>}
                  </Stack>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                href={largeImage.identifier}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("gbif.viewFullSize")}
              </Button>
              <Button onClick={() => setLargeImage(null)}>{t("gbif.close")}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
